import {
  useEffect,
  useRef,
  useState,
} from 'react';

import type {
  DailyRoutine,
} from '../application/daily-routine.ts';

import type {
  HomeDashboard,
  HomeDashboardView,
} from '../application/home-dashboard.ts';

import type {
  CheckView,
  ErrorCode,
  SetCheckCommand,
} from '../domain/daily-routine.ts';

import {
  Button,
  Card,
  ErrorMessage,
  StatusLabel,
} from './primitives.tsx';

const messages: Record<ErrorCode, string> = {
  REVISION_CONFLICT:
    'El ítem cambió en otra pestaña. Consulta su estado antes de decidir de nuevo.',

  INVARIANT_CONFLICT:
    'La intención no coincide con la operación anterior. Consulta el estado.',

  STORAGE_QUOTA:
    'No se pudo guardar: almacenamiento sin espacio. Se conserva el último estado confirmado.',

  TRANSACTION_ABORTED:
    'No se pudo guardar. Se conserva el último estado confirmado. Puedes reintentar.',

  STORAGE_UNAVAILABLE:
    'No se pudo confirmar la operación. Puedes reintentar la misma intención.',

  CORRUPTION_DETECTED:
    'Los datos locales no son coherentes. La operación se ha detenido.',

  SCHEMA_UNSUPPORTED:
    'Este almacenamiento requiere otra versión de la aplicación.',

  UPGRADE_BLOCKED:
    'Otra pestaña bloquea la apertura del almacenamiento. Ciérrala y recarga.',

  DATASET_STALE:
    'El contexto de datos cambió. Consulta el estado antes de decidir de nuevo.',

  RESTORE_IN_PROGRESS:
    'El almacenamiento está protegido por una operación de recuperación.',

  NOT_FOUND:
    'No se encuentra el ítem. No se ha creado ningún registro nuevo.',

  VALIDATION_ERROR:
    'La intención no es válida. No se ha guardado.',
};

const retryable =
  new Set<ErrorCode>([
    'STORAGE_QUOTA',
    'TRANSACTION_ABORTED',
    'STORAGE_UNAVAILABLE',
  ]);

function summaryError(code: string) {
  return (
    messages[code as ErrorCode] ??
    'No se pudo consultar este resumen. Vuelve a intentarlo.'
  );
}

export function Home({
  app,
  home,
  localDate,
  checkId,
  active,
}: {
  app: DailyRoutine;
  home: HomeDashboard | null;
  localDate: string;
  checkId: string;
  active: boolean;
}) {
  const [view, setView] =
    useState<CheckView | null>(null);

  const [summary, setSummary] =
    useState<HomeDashboardView | null>(null);

  const [summaryLoading, setSummaryLoading] =
    useState(false);

  const [busy, setBusy] =
    useState(false);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState<ErrorCode | null>(null);

  const [notice, setNotice] =
    useState('');

  const [requiresRead, setRequiresRead] =
    useState(false);

  const pending =
    useRef<SetCheckCommand | null>(null);

  const inFlight =
    useRef(false);

  const summaryRequest =
    useRef(0);

  const displayedDate = useRef(localDate);
  displayedDate.current = localDate;

  async function readSummary(
    localDate: string,
  ) {
    const request =
      ++summaryRequest.current;

    if (!home) {
      setSummary(null);
      setSummaryLoading(false);
      return;
    }

    setSummaryLoading(true);

    const result =
      await home.read(localDate);

    if (request !== summaryRequest.current) {
      return;
    }

    setSummary(result);
    setSummaryLoading(false);
  }

  async function read() {
    // Public summaries must refresh even when the DailyRoutine read/write
    // is already in flight (for example, focus racing a civil-day change).
    void readSummary(displayedDate.current);
    if (inFlight.current) {
      return;
    }

    inFlight.current = true;
    setLoading(true);
    setNotice('');

    /*
     * Home's civil date is independent from an optional
     * DailyRoutine item. Public module summaries must still
     * load when C-DH has no item for this day.
     */
    const result =
      await app.read(checkId);

    if (
      result.ok &&
      result.value.check.localDate === displayedDate.current
    ) {
      setView(result.value);
      setError(null);
      pending.current = null;
      setRequiresRead(false);
      setLoading(false);
      inFlight.current = false;
      return;
    }

    if (
      !result.ok &&
      result.code !== 'NOT_FOUND'
    ) {
      setError(result.code);
      setLoading(false);
      inFlight.current = false;
      return;
    }

    /* C-DH absence is not an error and creates no obligation. */
    setView(null);
    setError(null);
    pending.current = null;
    setRequiresRead(false);
    setLoading(false);
    inFlight.current = false;
  }

  useEffect(() => {
    if (active) {
      void read();
    }
  }, [
    active,
    app,
    checkId,
    home,
    localDate,
  ]);

  /*
   * Home is a projection. Returning to it or bringing
   * the app back to the foreground re-reads owners.
   */
  useEffect(() => {
    if (!active) {
      return;
    }

    const refresh = () => {
      if (
        document.visibilityState === 'visible'
      ) {
        void read();
      }
    };

    addEventListener(
      'focus',
      refresh,
    );

    document.addEventListener(
      'visibilitychange',
      refresh,
    );

    return () => {
      removeEventListener(
        'focus',
        refresh,
      );

      document.removeEventListener(
        'visibilitychange',
        refresh,
      );
    };
  }, [
    active,
    app,
    checkId,
    home,
    localDate,
  ]);

  async function submit() {
    if (
      inFlight.current ||
      loading ||
      requiresRead ||
      !view
    ) {
      return;
    }

    if (
      error &&
      !retryable.has(error)
    ) {
      return;
    }

    inFlight.current = true;

    setBusy(true);
    setError(null);
    setNotice('');

    pending.current ??=
      app.intend(
        view,
        !view.check.marked,
      );

    const result =
      await app.execute(
        pending.current,
      );

    if (result.ok) {
      pending.current = null;

      /*
       * The owner remains authoritative after write:
       * re-read instead of mutating a Home copy.
       */
      const current =
        await app.read(checkId);

      if (current.ok) {
        setView(current.value.check.localDate === displayedDate.current ? current.value : null);

        setNotice(
          'Guardado en este dispositivo.',
        );

        void readSummary(
          displayedDate.current,
        );
      } else {
        setError(current.code);

        setRequiresRead(true);

        void readSummary(displayedDate.current);

        setNotice(
          'Cambio confirmado. No se pudo actualizar la vista; vuelve a consultar.',
        );
      }
    } else {
      setError(result.code);
    }

    inFlight.current = false;
    setBusy(false);
  }

  const date =
    localDate;

  return (
    <div className="root-page">
      <div>
        <h1>Home</h1>

        <p className="secondary" data-testid="home-date">
          {date ??
            'Fecha pendiente de lectura'}
        </p>
      </div>

      <section
        aria-labelledby="next-action-title"
      >
        <h2 id="next-action-title">
          Siguiente acción
        </h2>

        <Card>
          {view ? (
            <>
              <h3>
                {view.check.label}
              </h3>

              <StatusLabel
                marked={
                  view.check.marked
                }
              />

              <Button
                data-testid="mark-button"
                busy={busy}
                disabled={
                  loading ||
                  requiresRead ||
                  (
                    !!error &&
                    !retryable.has(error)
                  )
                }
                aria-describedby={
                  error
                    ? 'operation-error'
                    : undefined
                }
                onClick={() =>
                  void submit()
                }
              >
                {pending.current &&
                error &&
                retryable.has(error)
                  ? 'Reintentar'
                  : view.check.marked
                    ? 'Desmarcar'
                    : 'Marcar'}
              </Button>
            </>
          ) : null}

          {!loading && !view && !error ? (
            <>
              <p data-testid="daily-routine-empty">
                Sin ítem de rutina personal para este día.
              </p>
              <p className="secondary">
                Sin siguiente acción personal registrada.
              </p>
            </>
          ) : null}

          {loading ? (
            <p role="status">
              Leyendo estado local…
            </p>
          ) : null}

          {error ? (
            <ErrorMessage>
              {messages[error]}
            </ErrorMessage>
          ) : null}

          {error && !busy ? (
            <Button
              data-testid="refresh-button"
              onClick={() =>
                void read()
              }
            >
              Consultar estado
            </Button>
          ) : null}

          <p
            role="status"
            aria-live="polite"
            data-testid="notice"
          >
            {notice}
          </p>
        </Card>
      </section>

      <section
        aria-labelledby="summary-title"
      >
        <h2 id="summary-title">
          Resumen diario
        </h2>

        <div
          className="catalog-list"
          data-testid="home-summary"
        >
          <Card>
            <h3>
              Rutina personal
            </h3>

            <p>
              {view
                ? view.check.marked
                  ? 'Marcado'
                  : 'No marcado'
                : loading
                  ? 'Estado pendiente de lectura'
                  : error
                    ? 'No se pudo consultar la rutina personal'
                    : 'Sin ítem para este día'}
            </p>

            {view ? (
              <p
                className="meta"
                data-testid="revision"
              >
                Revisión {
                  view.check.revision
                }
              </p>
            ) : null}
          </Card>

          <TrainingSummary
            summary={summary}
            loading={summaryLoading}
            date={date}
          />

          <NutritionSummary
            summary={summary}
            loading={summaryLoading}
            date={date}
          />

          <ProgressSummary
            summary={summary}
            loading={summaryLoading}
          />
        </div>
      </section>
    </div>
  );
}

function TrainingSummary({
  summary,
  loading,
  date,
}: {
  summary: HomeDashboardView | null;
  loading: boolean;
  date: string;
}) {
  const result =
    summary?.training;

  return (
    <Card>
      <h3>Training</h3>

      {loading ? (
        <p role="status">
          Actualizando Training…
        </p>
      ) : !result ? (
        <p>
          Resumen no disponible.
        </p>
      ) : !result.ok ? (
        <p
          role="alert"
          className="error"
        >
          {summaryError(
            result.code,
          )}
        </p>
      ) : result.value.kind ===
        'NO_PLAN' ? (
        <p>
          Descanso / no plan para este día.
        </p>
      ) : (
        <>
          <p>
            {result.value.routineName}
          </p>

          <p>
            {result.value.status}
            {' · '}
            fecha vigente {
              result.value.plannedLocalDate
            }
          </p>
        </>
      )}

      {result?.ok ? (
        <a
          className="settings-link"
          href={result.value.action.href}
          data-testid="home-training-action"
        >
          {result.value.action.label}
        </a>
      ) : (
        <a
          className="settings-link"
          href={`#Training/Hoy/${date}`}
        >
          Abrir Training
        </a>
      )}
    </Card>
  );
}

function NutritionSummary({
  summary,
  loading,
  date,
}: {
  summary: HomeDashboardView | null;
  loading: boolean;
  date: string;
}) {
  const result =
    summary?.nutrition;

  return (
    <Card>
      <h3>Nutrition</h3>

      {loading ? (
        <p role="status">
          Actualizando Nutrition…
        </p>
      ) : !result ? (
        <p>
          Resumen no disponible.
        </p>
      ) : !result.ok ? (
        <p
          role="alert"
          className="error"
        >
          {summaryError(
            result.code,
          )}
        </p>
      ) : (
        <>
          {!result.value.meals.length ? (
            <p>
              Sin comidas planificadas.
            </p>
          ) : (
            <ul>
              {result.value.meals.map(
                meal => (
                  <li
                    key={
                      meal.plannedMealId
                    }
                  >
                    <a
                      className="settings-link"
                      href={
                        `#Nutrition/Comida/` +
                        meal.plannedMealId
                      }
                    >
                      {meal.title}
                    </a>

                    {' · '}
                    {meal.status}
                  </li>
                ),
              )}
            </ul>
          )}

          <p>
            Improvisadas: {
              result.value.improvisedCount
            }
          </p>
        </>
      )}

      <a
        className="settings-link"
        href={`#Nutrition/Hoy/${date}`}
      >
        Abrir Nutrition
      </a>
    </Card>
  );
}

function ProgressSummary({
  summary,
  loading,
}: {
  summary: HomeDashboardView | null;
  loading: boolean;
}) {
  const result =
    summary?.progress;

  return (
    <Card>
      <h3>Progress</h3>

      {loading ? (
        <p role="status">
          Actualizando Progress…
        </p>
      ) : !result ? (
        <p>
          Resumen no disponible.
        </p>
      ) : !result.ok ? (
        <p
          role="alert"
          className="error"
        >
          {summaryError(
            result.code,
          )}
        </p>
      ) : (
        <>
          <p>
            {result.value.latestWeight
              ? (
                  `Último peso: ` +
                  `${result.value.latestWeight.value} kg · ` +
                  result.value.latestWeight.localDate
                )
              : 'Sin registros de peso.'}
          </p>

          <p>
            {
              result.value.recent.display !==
              undefined
                ? (
                    `Media descriptiva: ` +
                    `${result.value.recent.display} kg · ` +
                    `cobertura ${result.value.recent.count}/7 · ` +
                    `${result.value.recent.startLocalDate}–${result.value.recent.endLocalDate}`
                  )
                : (
                    `Datos insuficientes · ` +
                    `cobertura ${result.value.recent.count}/7 · ` +
                    `${result.value.recent.startLocalDate}–${result.value.recent.endLocalDate}`
                  )
            }
          </p>

          <p data-testid="home-weight-comparison">
            {
              result.value.displayDifference !==
              undefined
                ? (
                    `Comparación: ` +
                    `${result.value.displayDifference} kg · ` +
                    `cobertura reciente ${result.value.recent.count}/7 · ` +
                    `anterior ${result.value.previous.count}/7`
                  )
                : (
                    `Comparación no disponible · datos insuficientes · ` +
                    `cobertura reciente ${result.value.recent.count}/7 · ` +
                    `anterior ${result.value.previous.count}/7`
                  )
            }
          </p>
        </>
      )}

      <a
        className="settings-link"
        href="#Progress/Resumen"
      >
        Abrir Progress
      </a>
    </Card>
  );
}
